import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  getDateTimeObjFromDateTimeStr,
  getDateDetailsFromDateTimeObj,
} from 'utils/functions/dateTime';
import {
  fetchCampaignsList,
  fetchAddOrEditCampaignSubmission,
  setCampaignDetails,
} from 'modules/campaigns/actions';
import { campaignStatusEnum } from 'modules/campaigns/enums';
import CampaignsComponent from 'modules/campaigns/components/CampaignsComponent';

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const CampaignsContainer = ({ campaignsListApiCallBody }) => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPageFilter, setSelectedPageFilter] = useState(1);
  const [isActionPopupOpen, setIsActionPopupOpen] = useState(false);
  const [isActionPopupLoading, setIsActionPopupLoading] = useState(false);
  const [actionPopupStatus, setActionPopupStatus] = useState(0);
  const [actionPopupTarget, setActionPopupTarget] = useState('');
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  const searchBarInputTimeout = useRef(null);
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  const formatCampaignDate = dateStr => {
    const { dateObj } = getDateTimeObjFromDateTimeStr(dateStr, true);
    const { day, month, year } = getDateDetailsFromDateTimeObj(dateObj);
    return `${day} ${month} ${year}`;
  };
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  // eslint-disable-next-line no-unused-vars
  const submitSearchQuery = reset => {
    // setSelectedPageFilter(1);
    // const body = {
    //   ...ordersApiCallParams,
    // };
    // if (searchQuery) body.searchTerm = searchQuery;
    // if (reset) delete body.searchTerm;
    // setOrdersApiCallParams(body);
    // dispatch(fetchAllOrders(body));
  };

  const reloadCampaignsPage = () => {
    const body = {
      data: campaignsListApiCallBody,
    };

    dispatch(fetchCampaignsList(body));
  };

  const actionPopupConfirmBtnCallback = shouldReloadCampaigns => {
    setIsActionPopupOpen(false);
    setIsActionPopupLoading(false);
    setActionPopupStatus(0);
    setActionPopupTarget('');

    if (shouldReloadCampaigns) reloadCampaignsPage();
  };
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleEditCampaignBtnClick = campaign => {
    const {
      campaignKey,
      advertiser: { key: advertiserKey },
      endDate,
    } = campaign;
    const { dateObj } = getDateTimeObjFromDateTimeStr(endDate, true);

    dispatch(setCampaignDetails({ endDate: dateObj }));
    navigate(`/addCampaign/${campaignKey}?advertiserKey=${advertiserKey}`);
  };

  const handleAddAdvertBtnClick = campaignKey => {
    navigate(`/addAdvert?campaignKey=${campaignKey}`);
  };

  const handleViewAdvertsBtnClick = campaignKey => {
    navigate(`/adverts?campaignKey=${campaignKey}`);
  };

  const handleSearchBarInputChange = event => {
    const { value } = event.target;
    const searchQueryMinLength = 3;

    if (value.length > 30) return;
    setSearchQuery(value);

    if (searchBarInputTimeout.current) clearTimeout(searchBarInputTimeout.current);

    if (value && value.length >= searchQueryMinLength) {
      searchBarInputTimeout.current = setTimeout(() => {
        clearTimeout(searchBarInputTimeout.current);

        submitSearchQuery();
      }, 300);
    }
    // this condition checks if we are deleteing letters from search query and we reached
    // 2 characters, then we need to clear search query filter
    else if (searchQuery.length >= searchQueryMinLength && value.length < searchQueryMinLength)
      submitSearchQuery(true);
  };

  const handleOrderPaginationClick = selectedPageNumber => {
    setSelectedPageFilter(selectedPageNumber);

    const data = {
      ...campaignsListApiCallBody,
    };
    data.page = selectedPageNumber;
    const body = {
      data,
    };

    dispatch(fetchCampaignsList(body));
  };

  const handleActionBtnClick = (campaignKey, actionStatus) => {
    setIsActionPopupOpen(true);
    setActionPopupStatus(actionStatus);
    setActionPopupTarget(campaignKey);
  };

  const handleActionPopupConfirmBtnClick = () => {
    setIsActionPopupLoading(true);

    const body = {
      data: {
        status: campaignStatusEnum.getKeyByValue(actionPopupStatus),
      },
      campaignKey: actionPopupTarget,
      actionPopupCallbackAction: actionPopupConfirmBtnCallback,
    };

    dispatch(fetchAddOrEditCampaignSubmission(body));
  };

  const handleActionPopupDismissBtnClick = () => {
    setIsActionPopupOpen(false);
    setActionPopupStatus(0);
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  return (
    <CampaignsComponent
      searchQuery={searchQuery}
      selectedPageFilter={selectedPageFilter}
      isActionPopupOpen={isActionPopupOpen}
      isActionPopupLoading={isActionPopupLoading}
      actionPopupStatus={actionPopupStatus}
      formatCampaignDate={formatCampaignDate}
      handleEditCampaignBtnClick={handleEditCampaignBtnClick}
      handleAddAdvertBtnClick={handleAddAdvertBtnClick}
      handleViewAdvertsBtnClick={handleViewAdvertsBtnClick}
      handleSearchBarInputChange={handleSearchBarInputChange}
      handleOrderPaginationClick={handleOrderPaginationClick}
      handleActionBtnClick={handleActionBtnClick}
      handleActionPopupConfirmBtnClick={handleActionPopupConfirmBtnClick}
      handleActionPopupDismissBtnClick={handleActionPopupDismissBtnClick}
    />
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

CampaignsContainer.propTypes = {
  campaignsListApiCallBody: PropTypes.shape({}).isRequired,
};

CampaignsContainer.defaultProps = {};

export default CampaignsContainer;
